import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { formatDuration } from "@/features/Jobs/utils/runs";
import type { BackgroundTask } from "../types/backgroundTasks";

interface BackgroundTasksIndicatorProps {
  tasks: BackgroundTask[];
  onCancel: (taskId: string) => Promise<void>;
  /** Aberto por fora — o Parar sem turno em andamento abre a lista (AB#86889, task 87271). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A bolinha azul que pisca onde há tarefa em andamento (AB#86889, task 87270): nas conversas da
 * lista, no "Chat History" e no Chat do menu lateral. O número fica no título, ao passar o mouse.
 */
export function BackgroundTasksMarker({ count, className }: { count: number; className?: string }) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <span
      className={cn("inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-primary animate-cursor-glow", className)}
      title={`${t("playground.backgroundTasks.listMarker")}: ${count}`}
      aria-label={`${t("playground.backgroundTasks.listMarker")}: ${count}`}
      data-testid="background-tasks-marker"
    />
  );
}

/** O tempo passa sozinho enquanto o popover está aberto. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

/**
 * O selo "N tarefas em andamento" abaixo da caixa de mensagem (AB#86889, task 87270). Aberto, mostra
 * o que foi pedido em cada tarefa, há quanto tempo roda e o status, com o cancelamento ali mesmo.
 * Some quando não há tarefa viva; as contagens chegam pela sala SSE do usuário.
 */
export function BackgroundTasksIndicator({ tasks, onCancel, open, onOpenChange }: BackgroundTasksIndicatorProps) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const now = useNow(!!open || tasks.length > 0);

  if (tasks.length === 0) return null;

  const cancel = async (taskId: string) => {
    setCancelling(taskId);
    setFailed(null);
    try {
      await onCancel(taskId);
    } catch {
      // A tarefa segue viva: o usuário vê o aviso na linha e pode tentar de novo.
      setFailed(taskId);
    } finally {
      setCancelling(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-5 items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2",
            "text-[11px] font-medium text-primary transition-colors hover:bg-primary/15"
          )}
          data-testid="background-tasks-indicator"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          {t("playground.backgroundTasks.badge", { count: tasks.length })}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" className="w-80 p-0">
        <div className="border-b border-border/60 px-3 py-2 text-xs font-medium text-muted-foreground">
          {t("playground.backgroundTasks.title")}
        </div>
        <ul className="max-h-72 divide-y divide-border/50 overflow-y-auto">
          {tasks.map((task) => (
            <li key={task.id} className="flex items-start gap-2 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="line-clamp-2 text-sm text-foreground">
                  {task.request || t("playground.backgroundTasks.noRequest")}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {t(`playground.backgroundTasks.status.${task.status}`)} ·{" "}
                  {formatDuration(now - new Date(task.created_at).getTime())} · {task.short_id}
                </p>
                {failed === task.id && (
                  <p className="mt-0.5 text-xs text-destructive">{t("playground.backgroundTasks.cancelFailed")}</p>
                )}
              </div>
              {task.status === "watching" && (
                <button
                  type="button"
                  onClick={() => cancel(task.id)}
                  disabled={cancelling === task.id}
                  title={t("playground.backgroundTasks.cancel")}
                  className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40"
                >
                  {cancelling === task.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                  <span className="sr-only">{t("playground.backgroundTasks.cancel")}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
